/**
 * Both steps of the add/change email flow: the address step, then the
 * one-time code that confirms it. The URL owns which step is showing (see
 * `settings_.sign-in-methods.tsx`); this component owns only its two text
 * values. A taken address is an INLINE field error, per design prompt 44.4
 * point 1, never a modal. The code field deliberately mirrors
 * `sign-in.tsx`'s single-`Input` shape rather than a second, richer code
 * component.
 */
import {
  useEmailChangeConfirm,
  useEmailChangeRequest,
} from "@sportsmet/auth-sdk/private";
import { Button, Input } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import {
  extractErrorCode,
  showApiError,
  showOfflineError,
} from "../../errors/apiErrorBus";
import {
  CODE_FIELD_LABEL,
  CONFIRM_CODE_LABEL,
  EMAIL_FIELD_LABEL,
  EMAIL_SHAPE_ERROR,
  EMAIL_TAKEN_INLINE_ERROR,
  isEmailShapeValid,
  SEND_CODE_LABEL,
} from "./signInMethodsScreenLogic";

export type typeEmailChangeFormProps = {
  step: "add-email" | "code";
  requestId: string | undefined;
  onRequested: (args: { requestId: string }) => void;
  onConfirmed: () => void;
  onCancel: () => void;
};

export const EmailChangeForm = ({
  step,
  requestId,
  onRequested,
  onConfirmed,
  onCancel,
}: typeEmailChangeFormProps): ReactNode => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | undefined>(undefined);
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | undefined>(undefined);
  const request = useEmailChangeRequest();
  const confirm = useEmailChangeConfirm();

  async function handleSendCode(): Promise<void> {
    const trimmed = email.trim();
    if (!isEmailShapeValid(trimmed)) {
      setEmailError(EMAIL_SHAPE_ERROR);
      return;
    }
    setEmailError(undefined);
    let result;
    try {
      result = await request.mutateAsync({ newEmail: trimmed });
    } catch {
      showOfflineError();
      return;
    }
    if (extractErrorCode(result) === "CONFLICT") {
      setEmailError(EMAIL_TAKEN_INLINE_ERROR);
      return;
    }
    if (showApiError(result)) return;
    const newRequestId = result.data?.requestId;
    if (newRequestId !== undefined) onRequested({ requestId: newRequestId });
  }

  async function handleConfirmCode(): Promise<void> {
    if (requestId === undefined) return;
    setCodeError(undefined);
    let result;
    try {
      result = await confirm.mutateAsync({ requestId, code });
    } catch {
      showOfflineError();
      return;
    }
    if (extractErrorCode(result) === "CONFLICT") {
      setCodeError(EMAIL_TAKEN_INLINE_ERROR);
      return;
    }
    if (showApiError(result)) return;
    onConfirmed();
  }

  if (step === "code") {
    return (
      <div className="flex flex-col gap-4">
        <Input
          label={CODE_FIELD_LABEL}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          required
          value={code}
          error={codeError}
          onChange={(event) => {
            setCode(event.target.value);
          }}
          data-testid="email-change-code-input"
        />
        <Button
          data-testid="email-change-confirm"
          loading={confirm.isPending}
          loadingLabel="Confirming the code"
          onClick={() => {
            void handleConfirmCode();
          }}
        >
          {CONFIRM_CODE_LABEL}
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Input
        label={EMAIL_FIELD_LABEL}
        type="email"
        inputMode="email"
        autoComplete="email"
        required
        value={email}
        error={emailError}
        onChange={(event) => {
          setEmail(event.target.value);
        }}
        data-testid="email-change-input"
      />
      <Button
        data-testid="email-change-send"
        loading={request.isPending}
        loadingLabel="Sending a sign-in code"
        onClick={() => {
          void handleSendCode();
        }}
      >
        {SEND_CODE_LABEL}
      </Button>
      <Button variant="secondary" onClick={onCancel}>
        Cancel
      </Button>
    </div>
  );
};
